import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, ArrowRight, Sparkles, LayoutGrid } from "lucide-react";
import api from "../api";
import { categoryMeta } from "../mock";
import { useEnabledCategories } from "../hooks/useEnabledCategories";
import { useLocationFilter } from "../context/LocationContext";
import { useLang } from "../context/LanguageContext";
import { catLabel } from "../utils/categoryLabel";
import { useSeo } from "../hooks/useSeo";

const isLive = (d) => !d.expiresAt || new Date(d.expiresAt) > new Date();
const Categories = () => {
  const tiles = useEnabledCategories().filter((c) => !["More", "Near Expiry"].includes(c.name));
  const navigate = useNavigate();
  const { t } = useLang();
  const { country, city } = useLocationFilter();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  useSeo({ title: t("cat.exploreByCategory"), description: t("cat.subGlobal") });

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (country) params.country = country;
    if (city) params.city = city;
    api.get("/deals", { params }).then((r) => setDeals(r.data.filter(isLive))).catch(() => {}).finally(() => setLoading(false));
  }, [country, city]);

  const counts = useMemo(() => {
    const m = {};
    for (const d of deals) {
      const name = categoryMeta(d.category).name;
      m[name] = (m[name] || 0) + 1;
    }
    return m;
  }, [deals]);

  const nearExpiryCount = useMemo(() => deals.filter((d) => d.nearExpiry).length, [deals]);
  const scope = city || country || "everywhere";
  const go = (name) => navigate("/browse", { state: { category: name } });

  return (
    <div className="space-y-7 lp-fade-up">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-[#0f2a1a] text-white p-7 sm:p-9" data-testid="categories-hero">
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#16a34a]/40 blur-3xl" />
        <div className="absolute right-24 bottom-0 h-40 w-40 rounded-full bg-emerald-400/20 blur-2xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-emerald-200 backdrop-blur">
            <Sparkles size={13} /> {t("cat.exploreByCategory")}
          </span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">{t("cats.heading")}</h1>
          <p className="mt-2 max-w-lg text-sm text-emerald-100/80">
            {country || city ? t("cat.subScoped", { scope }) : t("cat.subGlobal")}
          </p>
          <div className="mt-5 flex items-center gap-2 text-sm">
            <span className="rounded-full bg-white/95 px-4 py-1.5 font-bold text-[#0f2a1a]" data-testid="hero-total-live">
              {loading ? "…" : t("cat.nLiveDeals", { n: deals.length })}
            </span>
            <span className="rounded-full border border-white/25 px-4 py-1.5 font-semibold text-white/90">
              {t("cat.nCategories", { n: tiles.length })}
            </span>
          </div>
        </div>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="category-tiles">
        {/* All deals — featured wide tile */}
        <button
          type="button"
          onClick={() => navigate("/browse")}
          data-testid="category-tile-all"
          className="group relative col-span-2 sm:col-span-1 overflow-hidden text-left rounded-3xl p-6 bg-gradient-to-br from-[#16a34a] to-[#0d7a37] text-white shadow-lg shadow-emerald-900/20 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
        >
          <span className="absolute -right-6 -bottom-6 h-24 w-24 rounded-full bg-white/15 blur-xl" />
          <span className="relative h-12 w-12 rounded-2xl flex items-center justify-center bg-white/20 backdrop-blur">
            <LayoutGrid size={24} />
          </span>
          <h3 className="relative font-extrabold text-lg mt-4">{t("cats.allDeals")}</h3>
          <p className="relative text-sm text-emerald-50/90 mt-0.5" data-testid="count-all">{loading ? "…" : t("cat.nLiveRightNow", { n: deals.length })}</p>
          <span className="relative mt-4 inline-flex items-center gap-1 text-sm font-bold">
            {t("cat.browseAll")} <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </button>

        {tiles.map((c) => {
          const Icon = c.icon;
          const n = counts[c.name] || 0;
          return (
            <button
              key={c.name}
              type="button"
              onClick={() => go(c.name)}
              data-testid={`category-tile-${c.name}`}
              className="group relative overflow-hidden text-left bg-card border border-border rounded-3xl p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              style={{ "--cat": c.color }}
            >
              <span
                className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100"
                style={{ backgroundColor: c.color }}
              />
              <span
                className="absolute inset-x-0 top-0 h-1 rounded-t-3xl scale-x-0 origin-left transition-transform duration-300 group-hover:scale-x-100"
                style={{ backgroundColor: c.color }}
              />
              <span
                className="relative h-14 w-14 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                style={{ backgroundColor: c.bg, color: c.color }}
              >
                <Icon size={26} />
              </span>
              <h3 className="relative font-bold text-foreground mt-4 transition-colors" style={{ }}>
                <span className="group-hover:text-[color:var(--cat)] transition-colors">{catLabel(c.name, t)}</span>
              </h3>
              <div className="relative mt-1.5 flex items-center justify-between">
                <span
                  className="text-xs font-bold px-2.5 py-1 rounded-full"
                  style={{ backgroundColor: c.bg, color: c.color }}
                  data-testid={`count-${c.name}`}
                >
                  {loading ? "…" : t("bl.nDeals", { n })}
                </span>
                <ArrowRight size={16} className="text-muted-foreground opacity-0 -translate-x-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0" style={{ color: c.color }} />
              </div>
            </button>
          );
        })}

        {/* Near Expiry — urgent tile */}
        <button
          type="button"
          onClick={() => navigate("/near-expiry")}
          data-testid="category-tile-near-expiry"
          className="group relative col-span-2 sm:col-span-1 overflow-hidden text-left rounded-3xl p-5 bg-gradient-to-br from-[#e11d48] to-[#be123c] text-white shadow-lg shadow-rose-900/20 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
        >
          <span className="absolute -right-6 -bottom-6 h-24 w-24 rounded-full bg-white/15 blur-xl" />
          <span className="relative h-14 w-14 rounded-2xl flex items-center justify-center bg-white/20 backdrop-blur">
            <CalendarClock size={26} />
          </span>
          <h3 className="relative font-extrabold text-lg mt-4">{t("cats.endingSoon")}</h3>
          <p className="relative text-sm text-rose-50/90 mt-0.5" data-testid="count-near-expiry">{loading ? "…" : `${nearExpiryCount} deal${nearExpiryCount === 1 ? "" : "s"} about to expire`}</p>
          <span className="relative mt-3 inline-flex items-center gap-1 text-sm font-bold">
            {t("cat.grabThemFast")} <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </button>
      </div>
    </div>
  );
};

export default Categories;
